import { Space, Spin, Table } from 'antd';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import UserName from '@/components/User/Name';
import { getCouponStatusDesc, useFetchCouponUserList } from '@/models/coupon';
import { batchQueryBasicInfo, User } from '@/models/user';
import { getUserAvatar } from '@/utils/uiHelper';

type Props = {
  couponId?: number;
  userId?: number;
  pageSize?: number;
};

const CouponUserTable: React.FC<Props> = ({ couponId, userId, pageSize = 10 }: Props) => {
  const [, , helpers] = useContext(BaseInfoContext);
  const { payload, setPayload, send, loaded, loading, error } = useFetchCouponUserList();
  const [userInfo, setUserInfo] = useState<{ [id: number]: User }>({});

  const getData = useCallback(
    (params = {}) => {
      send(Object.assign({ page: 1, limit: pageSize, couponId, userId }, params));
    },
    [couponId, userId, send, pageSize]
  );

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (payload && payload.list && payload.list.length && !userId) {
      // 有数据且未指定用户时，查询用户信息
      const ids = new Set(payload.list.map(item => item.userId));
      batchQueryBasicInfo(Array.from(ids)).then(userList => {
        setUserInfo(
          userList.reduce((acc, cur) => {
            acc[cur.id] = cur;
            return acc;
          }, {} as { [id: number]: User })
        );
      });
    }
  }, [userId, payload]);

  const columns = useMemo(() => {
    return [
      !couponId
        ? {
            title: '优惠券',
            align: 'center' as 'center',
            width: 100,
            dataIndex: 'name',
            fixed: 'left' as 'left',
          }
        : null,
      !userId
        ? {
            title: '用户',
            align: 'left' as 'left',
            width: 130,
            dataIndex: 'userId',
            fixed: 'left' as 'left',
            render: (_, record) => {
              const user = userInfo[record.userId];
              return (
                <div
                  onClick={() => {
                    helpers.showUserModal({ userId: user.id });
                  }}>
                  {user ? (
                    <Space>
                      {getUserAvatar(user)}
                      <UserName user={user} />
                    </Space>
                  ) : (
                    <Spin />
                  )}
                </div>
              );
            },
          }
        : null,
      {
        title: '状态',
        dataIndex: 'status',
        width: 120,
        render: record => {
          return getCouponStatusDesc(record);
        },
      },
      {
        title: '领取时间',
        dataIndex: 'addTime',
      },
      {
        title: '可用期-起',
        dataIndex: 'consumeStartTime',
      },
      {
        title: '可用期-止',
        dataIndex: 'consumeEndTime',
      },
      {
        title: '发放渠道',
        dataIndex: 'distributeChannel',
        width: 90,
        align: 'center' as const,
        render: (_, record) => {
          return record.distributeChannel === 1 ? '经营端' : '小程序';
        },
      },
      // {
      //   title: '操作',
      //   key: 'action',
      //   align: 'center' as 'center',
      //   width: 130,
      //   render: (_, coupon) => {
      //     return (
      //       <Space size={5} direction={'vertical'}>
      //         x
      //       </Space>
      //     );
      //   },
      // },
    ].filter(Boolean);
  }, [couponId, userId, userInfo]);

  return (
    <Table
      bordered
      loading={loading}
      scroll={{ x: 'max-content' }}
      dataSource={payload?.list}
      columns={columns}
      rowKey={`id`}
      pagination={{
        ...payload?.page,
        showTotal: total => <>共{total}条记录</>,
        size: 'default',
        onChange: page => {
          getData({ page: page });
        },
      }}
    />
  );
};

export default CouponUserTable;
